import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  TableContainer,
  Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import FolderIcon from "@mui/icons-material/Folder";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import ArticleIcon from "@mui/icons-material/Article";
import { API_URL } from "./config";

const ManageRolePageAccess = () => {
  const token = localStorage.getItem("auth");

  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const MODULE_ALT_1 = "rgba(25,118,210,0.045)";
  const MODULE_ALT_2 = "rgba(240,120,40,0.055)";
  const SUBMENU_BG = "rgba(255,255,255,0.62)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [access, setAccess] = useState({});
  const [message, setMessage] = useState("");

  const authHeader = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const loadRoles = async () => {
    const res = await axios.get(`${API_URL}/api/admin/access/roles`, authHeader);
    setRoles(res.data || []);
  };

  const loadPages = async () => {
    const res = await axios.get(`${API_URL}/api/admin/access/pages`, authHeader);
    setPages(res.data || []);
  };

  const loadRoleAccess = async (roleId) => {
    const res = await axios.get(
      `${API_URL}/api/admin/access/role/${roleId}`,
      authHeader
    );

    const map = {};
    for (const row of res.data || []) {
      map[row.PageID] = {
        CanView: Number(row.CanView) === 1,
        CanCreate: Number(row.CanCreate) === 1,
        CanEdit: Number(row.CanEdit) === 1,
        CanDelete: Number(row.CanDelete) === 1,
        CanApprove: Number(row.CanApprove) === 1,
      };
    }

    setAccess(map);
  };

  useEffect(() => {
    loadRoles();
    loadPages();
  }, []);

  useEffect(() => {
    if (selectedRoleId) loadRoleAccess(selectedRoleId);
    else setAccess({});
  }, [selectedRoleId]);

const groupedPages = useMemo(() => {
  const modules = [];

  pages.forEach((p) => {
    const moduleKey = p.ModuleID || p.ModuleCode || p.ModuleName || "OTHER";

    let module = modules.find((m) => m.moduleKey === moduleKey);
    if (!module) {
      module = {
        moduleKey,
        moduleName: p.ModuleName || p.ModuleCode || "Other",
        pages: [],
      };
      modules.push(module);
    }

    module.pages.push(p);
  });

  return modules.map((module, moduleIndex) => {
    const sortedPages = [...module.pages].sort((a, b) => {
      const sortA = Number(a.SortOrder ?? 999);
      const sortB = Number(b.SortOrder ?? 999);

      if (sortA !== sortB) return sortA - sortB;
      return String(a.PageName || "").localeCompare(String(b.PageName || ""));
    });

    const rows = [];
    const usedChildIds = new Set();

    sortedPages.forEach((p) => {
      const isChild = !!p.ParentPageID;

      if (isChild) return;

      const isSubmenuHeader = !p.RoutePath;

      if (isSubmenuHeader) {
        const children = sortedPages
          .filter((child) => Number(child.ParentPageID) === Number(p.PageID))
          .sort((a, b) => {
            const sortA = Number(a.SortOrder ?? 999);
            const sortB = Number(b.SortOrder ?? 999);

            if (sortA !== sortB) return sortA - sortB;
            return String(a.PageName || "").localeCompare(String(b.PageName || ""));
          });

        rows.push({
          ...p,
          rowType: "submenuHeader",
          childrenCount: children.length,
        });

        children.forEach((child) => {
          usedChildIds.add(Number(child.PageID));

          rows.push({
            ...child,
            rowType: "submenuPage",
            parentName: p.PageName,
          });
        });
      } else {
        rows.push({
          ...p,
          rowType: "directPage",
        });
      }
    });

    sortedPages
      .filter((p) => p.ParentPageID && !usedChildIds.has(Number(p.PageID)))
      .forEach((child) => {
        rows.push({
          ...child,
          rowType: "submenuPage",
          parentName: "Unmapped Submenu",
        });
      });

    return {
      ...module,
      moduleIndex,
      rows,
    };
  });
}, [pages]);
  
  const getPageIcon = (rowType) => {
    if (rowType === "submenuHeader") return <FolderIcon fontSize="small" />;
    if (rowType === "submenuPage") return <SubdirectoryArrowRightIcon fontSize="small" />;
    return <ArticleIcon fontSize="small" />;
  };
  
  const getPageTypeChip = (p) => {
    if (p.rowType === "submenuHeader") {
      return (
        <Chip
          label="Submenu"
          size="small"
          sx={{
            height: 22,
            fontSize: "0.68rem",
            fontWeight: 900,
            color: "#fff",
            backgroundColor: THEME_ORANGE,
          }}
        />
      );
    }
  
    if (p.rowType === "submenuPage") {
      return (
        <Chip
          label="Submenu Page"
          size="small"
          sx={{
            height: 22,
            fontSize: "0.68rem",
            fontWeight: 900,
            color: THEME_ORANGE,
            backgroundColor: "rgba(240,120,40,0.10)",
            border: "1px solid rgba(240,120,40,0.35)",
          }}
        />
      );
    }
  
    return (
      <Chip
        label="Direct Page"
        size="small"
        sx={{
          height: 22,
          fontSize: "0.68rem",
          fontWeight: 900,
          color: "rgba(25,118,210,0.95)",
          backgroundColor: "rgba(25,118,210,0.08)",
          border: "1px solid rgba(25,118,210,0.20)",
        }}
      />
    );
  };

  const emptyPerms = {
    CanView: false,
    CanCreate: false,
    CanEdit: false,
    CanDelete: false,
    CanApprove: false,
  };
  
  const getPageById = (pageId) =>
    pages.find((p) => Number(p.PageID) === Number(pageId));
  
  const getChildrenByParentId = (parentPageId) =>
    pages.filter((p) => Number(p.ParentPageID) === Number(parentPageId));
  
  const toggle = (pageId, field) => {
    setAccess((prev) => {
      const page = getPageById(pageId);
  
      const current = {
        ...emptyPerms,
        ...(prev[pageId] || {}),
      };
  
      const nextValue = !current[field];
  
      const next = {
        ...prev,
        [pageId]: {
          ...current,
          [field]: nextValue,
          ...(field !== "CanView" && nextValue ? { CanView: true } : {}),
        },
      };
  
      // If submenu child page is selected, auto-select parent submenu folder
      if (page?.ParentPageID && nextValue) {
        const parentId = page.ParentPageID;
  
        next[parentId] = {
          ...emptyPerms,
          ...(next[parentId] || {}),
          CanView: true,
        };
      }
  
      // If submenu child page is unselected, unselect parent only if no child remains selected
      if (page?.ParentPageID && !nextValue) {
        const parentId = page.ParentPageID;
        const siblings = getChildrenByParentId(parentId);
  
        const anySiblingSelected = siblings.some((child) => {
          if (Number(child.PageID) === Number(pageId)) {
            return Boolean(next[pageId]?.CanView);
          }
  
          return Boolean(next[child.PageID]?.CanView);
        });
  
        if (!anySiblingSelected) {
          next[parentId] = {
            ...emptyPerms,
            ...(next[parentId] || {}),
            CanView: false,
          };
        }
      }
  
      return next;
    });
  };

  const save = async () => {
    const payload = {
      roleId: selectedRoleId,
      access: Object.entries(access).map(([pageId, perms]) => ({
        pageId: Number(pageId),
        ...perms,
      })),
    };

    await axios.post(`${API_URL}/api/admin/access/role/save`, payload, authHeader);
    setMessage("Access saved successfully");
  };



  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
            Manage Role Page Access
          </Typography>
          <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
            Assign page permissions to each role
          </Typography>
        </Box>

        <Box sx={bodySx}>
          {message && (
            <Alert
              severity="success"
              onClose={() => setMessage("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(46,125,50,0.25)",
              }}
            >
              {message}
            </Alert>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Role"
                  fullWidth
                  size="small"
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  sx={inputSx}
                >
                  {roles.map((r) => (
                    <MenuItem key={r.RoleID} value={r.RoleID}>
                      {r.RoleName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!selectedRoleId}
                  onClick={save}
                  startIcon={<SaveIcon />}
                  sx={primaryBtnSx}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={tableWrapSx}>
            <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
              <TableContainer component="div">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell sx={{ minWidth: 180 }}>Module</TableCell>
                      <TableCell sx={{ minWidth: 360 }}>Page / Menu</TableCell>
                      <TableCell sx={{ minWidth: 260 }}>Route</TableCell>
                      <TableCell align="center">View</TableCell>
                     {/* <TableCell align="center">Create</TableCell>
                      <TableCell align="center">Edit</TableCell>
                      <TableCell align="center">Delete</TableCell>
                      <TableCell align="center">Approve</TableCell>
                      */}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {groupedPages.map((module) => {
                      const moduleBg =
                        module.moduleIndex % 2 === 0 ? MODULE_ALT_1 : MODULE_ALT_2;

                      return (
                        <React.Fragment key={module.moduleKey}>
                          <TableRow
                            sx={{
                              backgroundColor:
                                module.moduleIndex % 2 === 0
                                  ? "rgba(25,118,210,0.13)"
                                  : "rgba(240,120,40,0.13)",
                            }}
                          >
                            <TableCell colSpan={8}>
                              <Chip
                                label={module.moduleName}
                                size="small"
                                sx={{
                                  fontWeight: 900,
                                  color: "rgba(25,118,210,0.98)",
                                  backgroundColor: "#fff",
                                  border: "1px solid rgba(25,118,210,0.25)",
                                }}
                              />
                            </TableCell>
                          </TableRow>

                          {module.rows.map((p, idx) => {
                            const row = access[p.PageID] || {};
                            //const isMenu = p.rowType === "menu";
                            //const isSubmenu = p.rowType === "submenu";
                            const isSubmenuHeader = p.rowType === "submenuHeader";
                            const isSubmenuPage = p.rowType === "submenuPage";
                            const isDirectPage = p.rowType === "directPage";

                            return (
                              <TableRow
                                key={p.PageID}
                                sx={{
                                  backgroundColor: isSubmenuPage ? SUBMENU_BG : moduleBg,
                                  "&:hover": { backgroundColor: ROW_HOVER },
                                  "& td": {
                                    borderBottom:
                                      idx === module.rows.length - 1
                                        ? "2px solid rgba(25,118,210,0.22)"
                                        : "1px solid rgba(25,118,210,0.08)",
                                  },
                                }}
                              >
                                <TableCell />

                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      pl: isSubmenuPage ? 4 : 0,
                                      position: "relative",
                                    }}
                                  >
                                    {isSubmenuPage && (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          left: 18,
                                          top: -12,
                                          width: 22,
                                          height: 34,
                                          borderLeft: "2px solid rgba(240,120,40,0.45)",
                                          borderBottom: "2px solid rgba(240,120,40,0.45)",
                                          borderBottomLeftRadius: 8,
                                        }}
                                      />
                                    )}

                                    <Box
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: isSubmenuPage ? "50%" : 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: isSubmenuPage
                                          ? THEME_ORANGE
                                          : "rgba(25,118,210,0.95)",
                                        backgroundColor: isSubmenuPage
                                          ? "rgba(240,120,40,0.10)"
                                          : "rgba(25,118,210,0.09)",
                                        border: isSubmenuPage
                                          ? "1px solid rgba(240,120,40,0.30)"
                                          : "1px solid rgba(25,118,210,0.20)",
                                        zIndex: 1,
                                      }}
                                    >
                                      {getPageIcon(p.rowType)}
                                    </Box>

                                    <Box sx={{ minWidth: 0 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: isSubmenuHeader ? 900 : isSubmenuPage ? 750 : 800,
                                            fontSize: isSubmenuHeader
                                              ? "0.93rem"
                                              : isSubmenuPage
                                              ? "0.84rem"
                                              : "0.88rem",
                                            color: isSubmenuHeader
                                              ? "rgba(25,118,210,0.98)"
                                              : isSubmenuPage
                                              ? "rgba(0,0,0,0.78)"
                                              : "rgba(0,0,0,0.85)",
                                          }}
                                        >
                                          {p.PageName}
                                        </Typography>

                                        {getPageTypeChip(p)}
                                      </Box>

                                      {isSubmenuHeader && p.childrenCount > 0 && (
                                        <Typography
                                          sx={{
                                            fontSize: "0.72rem",
                                            color: "text.secondary",
                                            mt: 0.2,
                                          }}
                                        >
                                          Contains {p.childrenCount} submenu item
                                          {p.childrenCount > 1 ? "s" : ""}
                                        </Typography>
                                      )}

                                      {isSubmenuPage && p.parentName && (
                                        <Typography
                                          sx={{
                                            fontSize: "0.72rem",
                                            color: "text.secondary",
                                            mt: 0.2,
                                          }}
                                        >
                                          Under {p.parentName}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>

                                <TableCell sx={{ color: "text.secondary" }}>
                                  {p.RoutePath || "-"}
                                </TableCell>

                                {[
                                  "CanView",
                                 // "CanCreate",
                                 // "CanEdit",
                                 // "CanDelete",
                                 // "CanApprove",
                                ].map((field) => (
                                  <TableCell align="center" key={field}>
                                    <Checkbox
                                      checked={Boolean(row[field])}
                                      onChange={() => toggle(p.PageID, field)}
                                      disabled={!selectedRoleId}
                                      sx={checkboxSx}
                                    />
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default ManageRolePageAccess;